import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getCalibrators,
  getCalibrator,
  createCalibrator,
  updateCalibrator,
  deleteCalibrator,
} from '../api';
import type { CalibratorsFilters, CreateCalibratorInput } from '../types';

const CDC_REFRESH_DELAYS_MS = [1500, 5000, 9000] as const;

// =============================================================================
// Query Keys Factory
// =============================================================================

export const calibratorKeys = {
  all: ['calibrators'] as const,
  lists: () => [...calibratorKeys.all, 'list'] as const,
  list: (filters?: CalibratorsFilters) =>
    [...calibratorKeys.lists(), filters] as const,
  detail: (id: string) => [...calibratorKeys.all, 'detail', id] as const,
};

function refreshCalibratorQueries(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: calibratorKeys.all });

  for (const delay of CDC_REFRESH_DELAYS_MS) {
    window.setTimeout(() => {
      void queryClient.refetchQueries({
        queryKey: calibratorKeys.all,
        type: 'active',
      });
    }, delay);
  }
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch calibrators list with filtering and pagination
 */
export function useCalibratorsList(filters: CalibratorsFilters = {}) {
  return useQuery({
    queryKey: calibratorKeys.list(filters),
    queryFn: () => getCalibrators(filters),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch a single calibrator by ID
 */
export function useCalibrator(id: string) {
  return useQuery({
    queryKey: calibratorKeys.detail(id),
    queryFn: () => getCalibrator(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

export function useCreateCalibrator() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCalibratorInput) => createCalibrator(data),
    onSuccess: () => {
      refreshCalibratorQueries(queryClient);
      toast.success('Calibrator created');
    },
    onError: (error) => {
      toast.error('Failed to create calibrator', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

export function useUpdateCalibrator() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateCalibratorInput>;
    }) => updateCalibrator(id, data),
    onSuccess: () => {
      refreshCalibratorQueries(queryClient);
      toast.success('Calibrator updated');
    },
    onError: (error) => {
      toast.error('Failed to update calibrator', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}

export function useDeleteCalibrator() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCalibrator(id),
    onSuccess: () => {
      refreshCalibratorQueries(queryClient);
      toast.success('Calibrator deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete calibrator', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });
}
